'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import { ProductCard } from '@//components/ProductCard';
import useBrandings from '@//data/search/brandings/useBrandings';
import { useLegacyTrackExposure } from '@//data/tracking/useLegacyTrackExposure';
import { useLegacyTrackProductClick } from '@//data/tracking/useLegacyTrackProductClick';
import { useViewportTracking } from '@//hooks/useViewportTracking';
import VehicleSubscriptionButton from '../VehicleSubscriptionButton';
import useProductCardContainerDetails from './useProductCardContainerDetails';
import useProductCardContainerImage, {
  type ProductCardImagePreset,
} from './useProductCardContainerImage';
import useProductCardContainerPrices from './useProductCardContainerPrices';

interface ProductCardContainerProps {
  item: ElasticListVehicle;
  index: number;
  parameters?: string;
  itemPage: string;
  currency: string;
  testId: string;
  loading?: React.ImgHTMLAttributes<HTMLImageElement>['loading'];
  imagePreset?: ProductCardImagePreset;
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

const ProductCardContainer = ({
  item,
  index,
  parameters,
  itemPage,
  currency,
  testId,
  loading,
  imagePreset,
  headingLevel,
  onLinkClick,
}: ProductCardContainerProps) => {
  const t = useTranslations('other.common');
  const { trackLegacyExposure } = useLegacyTrackExposure();
  const { trackLegacyProductClick } = useLegacyTrackProductClick();

  const image = useProductCardContainerImage(item, loading, imagePreset);
  const details = useProductCardContainerDetails(item);
  const { mainPrice, otherPrices } = useProductCardContainerPrices(item, {
    currency,
  });
  //const isNew = useProductCardContainerIsNew(item.itemPublished);
  const isNew = false;
  const brandingsQuery = useBrandings();

  const handleViewportEnter = useCallback(() => {
    trackLegacyExposure(item, {
      index,
      parameters,
    });
  }, [trackLegacyExposure, item._id, index]);

  const { elementRef } = useViewportTracking({
    onEnterViewport: handleViewportEnter,
    threshold: 0.5,
    trackOnce: true,
  });

  const manufacturerPackaging = item.hasManufacturerPackaging
    ? brandingsQuery.data?.find(
        (b) => b.identifier === item.manufacturer && b.category.includes('Manufacture packaging'),
      )
    : undefined;

  const brandingImage = manufacturerPackaging?.logo.file.url
    ? {
        src: `${manufacturerPackaging?.logo.file.url}?w=85&h=24`,
        srcSet: `${manufacturerPackaging?.logo.file.url}?w=170&h=48 2x`,
        alt: manufacturerPackaging?.name,
      }
    : undefined;

  const itemHref = useMemo(() => `${itemPage}/${item._id}`, [itemPage, item._id]);

  const handleLinkClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      trackLegacyProductClick({
        vehicle: item,
        information: { index },
      });
      onLinkClick?.(event);
    },
    [trackLegacyProductClick, item, index, onLinkClick],
  );

  return (
    <ProductCard
      ref={elementRef}
      href={itemHref}
      image={image}
      title={item.title}
      subTitle={item.shortDescription}
      details={details.length ? details : undefined}
      testId={testId}
      headingLevel={headingLevel}
      retailer={item.branches?.[0]?.name || t('unknownRetailer')}
      location={item.position?.city || t('unknownLocation')}
      mainPrice={mainPrice}
      otherPrice={otherPrices}
      manufacturerPackaingImage={brandingImage}
      inventoryStatus={item.inventoryStatusString}
      isNew={isNew}
      floatingAction={
        <VehicleSubscriptionButton item={{ type: 'ElasticListVehicle', vehicle: item }} />
      }
      onLinkClick={handleLinkClick}
      sold={item.status === 'Sold'}
    />
  );
};

export default ProductCardContainer;
